import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { PageIntro } from '../components/layout/PageIntro';
import { ExternalLink, Camera } from 'lucide-react';
import { useSeo } from '../hooks/useSeo';
import { PAGE_SEO } from '../seo/site';

const BildquellenPage = () => {
  useSeo(PAGE_SEO.bildquellen);
  const unsplashImages = [
    {
      imageId: '1517248135467-4c7edcad34c4',
      photographer: 'Jason Leung',
      photographerUrl: 'https://unsplash.com/@ninjason',
      description: 'Restaurant Interieur',
      usage: 'Reservierung / Gastro-Landschaft'
    },
    {
      imageId: '1600880292203-757bb62b4baf',
      photographer: 'Austin Distel',
      photographerUrl: 'https://unsplash.com/@austindistel',
      description: 'Team-Besprechung im Büro',
      usage: 'Personalplanung'
    },
    {
      imageId: '1526367790999-0150786686a2',
      photographer: 'Rowan Freeman',
      photographerUrl: 'https://unsplash.com/@rowanfreeman',
      description: 'Lieferfahrer mit Paket',
      usage: 'Lieferservice'
    },
    {
      imageId: '1460925895917-afdab827c52f',
      photographer: 'Carlos Muza',
      photographerUrl: 'https://unsplash.com/@kmuza',
      description: 'Analytics Dashboard auf Laptop',
      usage: 'Optimierung / Lösungen'
    },
    {
      imageId: '1432888498266-38ffec3eaf0a',
      photographer: 'William Iven',
      photographerUrl: 'https://unsplash.com/@firmbee',
      description: 'Laptop mit Notizen',
      usage: 'Website & Bewertungen'
    },
    {
      imageId: '1647427017067-8f33ccbae493',
      photographer: 'Clay Banks',
      photographerUrl: 'https://unsplash.com/@claybanks',
      description: 'Kartenzahlung Terminal',
      usage: 'Kassensystem & Kartenzahlung'
    },
    {
      imageId: '1609427955204-d0a737cb2c1a',
      photographer: 'Albert Hu',
      photographerUrl: 'https://unsplash.com/@alberthu',
      description: 'QR-Code scannen',
      usage: 'Smarte Order-Lösungen'
    },
    {
      imageId: '1565718253569-3156836e2ec0',
      photographer: 'Blake Wisz',
      photographerUrl: 'https://unsplash.com/@blakewisz',
      description: 'Kreditkarte in Hand',
      usage: 'Loyalty & Kundenbindung'
    },
    {
      imageId: '1611162617474-5b21e879e113',
      photographer: 'Alexander Shatov',
      photographerUrl: 'https://unsplash.com/@alexbemore',
      description: 'Social Media Icons',
      usage: 'Social Media'
    },
    {
      imageId: '1619462729367-e47da54ba992',
      photographer: 'Georgia de Lotz',
      photographerUrl: 'https://unsplash.com/@georgiadelotz',
      description: 'Instagram auf Smartphone',
      usage: 'Social Media (Portal)'
    },
    {
      imageId: '1593929976216-f746e488aa45',
      photographer: 'Markus Winkler',
      photographerUrl: 'https://unsplash.com/@markuswinkler',
      description: 'Datenanalyse',
      usage: 'Lösungen-Seite'
    },
    {
      imageId: '1567335632614-abebe179111e',
      photographer: 'Fikri Rasyid',
      photographerUrl: 'https://unsplash.com/@fikrirasyid',
      description: 'Restaurant Atmosphäre',
      usage: 'Lösungen-Seite'
    }
  ];

  const pexelsImages = [
    {
      imageId: '733851',
      photographerUrl: 'https://www.pexels.com/@tim-gouw-52608',
      photographer: 'Tim Gouw',
      description: 'Arbeitsplatz',
      usage: 'Lösungen-Seite'
    },
    {
      imageId: '3296547',
      photographerUrl: 'https://www.pexels.com/@cottonbro',
      photographer: 'cottonbro studio',
      description: 'Barista bei der Arbeit',
      usage: 'Lösungen-Seite'
    }
  ];

  return (
    <>
      <Header />
      <main data-testid="bildquellen-page">
        <PageIntro
          eyebrow="Rechtliches"
          title="Bildquellen"
          intro="Auf dieser Seite findest du eine Übersicht aller auf unserer Website verwendeten Stock-Fotos mit Angabe der jeweiligen Fotografen und Quellen."
        />
        <div className="wrap max-w-4xl sec--tight">
          <div className="space-y-12">
            {/* Unsplash Section */}
            <section>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-11 h-11 border-2 border-ink bg-ink flex items-center justify-center">
                  <Camera className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className="font-display font-bold text-2xl leading-none">Unsplash</h2>
                  <a 
                    href="https://unsplash.com" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-sm text-purple underline underline-offset-4 hover:text-deep flex items-center gap-1 mt-1"
                  >
                    unsplash.com
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              </div>
              
              <div className="card-ink overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-lilac border-b-2 border-ink">
                        <th className="text-left px-4 py-3 font-display font-bold uppercase tracking-wider text-xs">Fotograf</th>
                        <th className="text-left px-4 py-3 font-display font-bold uppercase tracking-wider text-xs">Beschreibung</th>
                        <th className="text-left px-4 py-3 font-display font-bold uppercase tracking-wider text-xs">Verwendung</th>
                        <th className="text-left px-4 py-3 font-display font-bold uppercase tracking-wider text-xs">Bild</th>
                        <th className="text-left px-4 py-3 font-display font-bold uppercase tracking-wider text-xs">Profil</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-ink/15">
                      {unsplashImages.map((image, index) => (
                        <tr key={index} className="hover:bg-off">
                          <td className="px-4 py-3 font-bold">{image.photographer}</td>
                          <td className="px-4 py-3 text-ink/80">{image.description}</td>
                          <td className="px-4 py-3 text-ink/80">{image.usage}</td>
                          <td className="px-4 py-3">
                            <a 
                              href={`https://images.unsplash.com/photo-${image.imageId}?w=1200`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-purple underline underline-offset-4 hover:text-deep flex items-center gap-1"
                            >
                              Ansehen
                              <ExternalLink className="w-3 h-3" />
                            </a>
                          </td>
                          <td className="px-4 py-3">
                            <a 
                              href={image.photographerUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-purple underline underline-offset-4 hover:text-deep flex items-center gap-1"
                            >
                              Profil
                              <ExternalLink className="w-3 h-3" />
                            </a>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </section>

            {/* Pexels Section */}
            <section>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-11 h-11 border-2 border-ink bg-[#05A081] flex items-center justify-center">
                  <Camera className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className="font-display font-bold text-2xl leading-none">Pexels</h2>
                  <a 
                    href="https://pexels.com" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-sm text-purple underline underline-offset-4 hover:text-deep flex items-center gap-1 mt-1"
                  >
                    pexels.com
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              </div>
              
              <div className="card-ink overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-lilac border-b-2 border-ink">
                        <th className="text-left px-4 py-3 font-display font-bold uppercase tracking-wider text-xs">Fotograf</th>
                        <th className="text-left px-4 py-3 font-display font-bold uppercase tracking-wider text-xs">Beschreibung</th>
                        <th className="text-left px-4 py-3 font-display font-bold uppercase tracking-wider text-xs">Verwendung</th>
                        <th className="text-left px-4 py-3 font-display font-bold uppercase tracking-wider text-xs">Bild</th>
                        <th className="text-left px-4 py-3 font-display font-bold uppercase tracking-wider text-xs">Profil</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-ink/15">
                      {pexelsImages.map((image, index) => (
                        <tr key={index} className="hover:bg-off">
                          <td className="px-4 py-3 font-bold">{image.photographer}</td>
                          <td className="px-4 py-3 text-ink/80">{image.description}</td>
                          <td className="px-4 py-3 text-ink/80">{image.usage}</td>
                          <td className="px-4 py-3">
                            <a 
                              href={`https://www.pexels.com/photo/${image.imageId}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-purple underline underline-offset-4 hover:text-deep flex items-center gap-1"
                            >
                              Ansehen
                              <ExternalLink className="w-3 h-3" />
                            </a>
                          </td>
                          <td className="px-4 py-3">
                            <a 
                              href={image.photographerUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-purple underline underline-offset-4 hover:text-deep flex items-center gap-1"
                            >
                              Profil
                              <ExternalLink className="w-3 h-3" />
                            </a>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </section>

            {/* License Note */}
            <section className="card-ink card-ink--lilac p-6 md:p-8">
              <h3 className="font-display font-bold text-2xl mb-3">Lizenzhinweis</h3>
              <p className="text-[1rem] leading-relaxed">
                Alle auf dieser Website verwendeten Bilder von Unsplash und Pexels werden unter den 
                jeweiligen kostenlosen Lizenzen der Plattformen genutzt. Die Unsplash-Lizenz und die 
                Pexels-Lizenz erlauben die kostenlose Nutzung für kommerzielle und nicht-kommerzielle 
                Zwecke ohne erforderliche Namensnennung. Wir nennen die Fotografen dennoch aus Respekt 
                vor ihrer kreativen Arbeit.
              </p>
            </section>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
};

export default BildquellenPage;
